import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Lightweight in-app navigation history stack replacing ?from= query propagation.
// Persistence: sessionStorage key 'navStack'. Each entry is a full path (pathname + optional search).
// On refresh: stack is restored; if empty, seed with current path.
// goBack: pops current and navigates to previous; if no previous, navigates to provided fallback.

const NavigationHistoryContext = createContext({
  stack: [],
  goBack: () => {},
});

export const NavigationHistoryProvider = ({ children, maxDepth = 50 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const stackRef = useRef([]);
  const initializedRef = useRef(false);

  // Initialize from sessionStorage once
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    try {
      const stored = sessionStorage.getItem('navStack');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          stackRef.current = parsed;
        }
      }
    } catch (e) {
      // ignore parse errors
    }
    if (stackRef.current.length === 0) {
      stackRef.current.push(location.pathname + (location.search || ''));
      sessionStorage.setItem('navStack', JSON.stringify(stackRef.current));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push on location change if different from top
  useEffect(() => {
    if (!initializedRef.current) return; // ensure init completed
    const current = location.pathname + (location.search || '');
    const stack = stackRef.current;
    if (stack[stack.length - 1] !== current) {
      stack.push(current);
      if (stack.length > maxDepth) {
        stack.splice(0, stack.length - maxDepth);
      }
      sessionStorage.setItem('navStack', JSON.stringify(stack));
    }
  }, [location, maxDepth]);

  const goBack = (fallback) => {
    const stack = stackRef.current;
    if (stack.length > 1) {
      // Remove current
      stack.pop();
      const target = stack[stack.length - 1];
      sessionStorage.setItem('navStack', JSON.stringify(stack));
      navigate(target);
    } else if (fallback) {
      navigate(fallback);
    } else {
      navigate('/');
    }
  };

  return (
    <NavigationHistoryContext.Provider value={{ stack: stackRef.current, goBack }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
};

export const useNavigationHistory = () => useContext(NavigationHistoryContext);
